import { AnyAction } from '@reduxjs/toolkit';
import { produce } from 'immer';
import { ThunkResult } from './index';

export interface StateShape {
    items: any[] | null;
    itemsLoading: boolean;
    itemsLoadError: any | null;
    templates: any[] | null;
    templatesLoading: boolean;
    templatesLoadError: any | null;
    itemVersions: any[] | null;
    itemVersionsLoading: boolean;
    revertedVersion: boolean;
    revertedVersionError: any | null;
    itemVersionsLoadError: any | null;
    selectedPages: string[];
    showDeletedItems: boolean;
}

export const defaultState: StateShape = {
    items: null,
    itemsLoading: false,
    itemsLoadError: null,
    templates: null,
    templatesLoading: false,
    templatesLoadError: null,
    itemVersions: null,
    itemVersionsLoading: false,
    revertedVersion: false,
    itemVersionsLoadError: null,
    revertedVersionError: null,
    selectedPages: [],
    showDeletedItems: false,
};

const ITEMS_LOAD_START = 'ITEMS_LOADING';
const ITEMS_LOAD_END = 'ITEMS_LOAD_END';

const TEMPLATES_LOAD_START = 'TEMPLATES_LOADING';
const TEMPLATES_LOAD_END = 'TEMPLATES_LOAD_END';

const ITEM_VERSIONS_LOAD_START = 'ITEM_VERSIONS_LOAD_START';
const ITEM_VERSIONS_LOAD_END = 'ITEM_VERSIONS_LOAD_END';

const SET_SELECTED_PAGES = 'SET_SELECTED_PAGES';
const REMOVE_SELECTED_PAGES = 'REMOVE_SELECTED_PAGES';
const REMOVE_ALL_SELECTED_PAGES = 'REMOVE_ALL_SELECTED_PAGES';

const REVERTED_VERSION = 'REVERTED_VERSION';

const SET_SHOW_DELETED_ITEMS = 'SET_SHOW_DELETED_ITEMS';

export const actionCreators = {
    loadPages: (
        folderName: string,
        filters: { idPattern?: string[]; authorsIn?: any } = {
            idPattern: [],
            authorsIn: [],
        },
        sort?: { field?: string; dir?: string },
        take?: number,
        skip?: number
    ): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch({ type: ITEMS_LOAD_START });

            try {
                const url = folderName
                    ? `/api/admin/reports/list${folderName}`
                    : `/api/admin/reports/`;
                // only absolute URLs are allowed!
                const results = await fetch(url, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `bearer ${state.auth.user?.access_token}`,
                    },
                    body: JSON.stringify({
                        deleted: state.adminPages.showDeletedItems,
                        sortBy: sort?.field || 'Id',
                        sortDirection: sort?.dir || 'asc',
                        take: take || 25,
                        skip: skip || 0,
                        ...filters,
                    }),
                });
                const items = await results.json();
                dispatch({ type: ITEMS_LOAD_END, items });
            } catch (err: any) {
                console.error(err);
                dispatch({
                    type: ITEMS_LOAD_END,
                    error: { message: err.message },
                });
            }
        };
    },

    loadTemplates: (): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch({ type: TEMPLATES_LOAD_START });

            try {
                const url = `/api/admin/reports/templates`;

                const results = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `bearer ${state.auth.user?.access_token}`,
                    },
                });
                const items = await results.json();
                dispatch({ type: TEMPLATES_LOAD_END, items });
            } catch (err: any) {
                console.error(err);
                dispatch({
                    type: TEMPLATES_LOAD_END,
                    error: { message: err.message },
                });
            }
        };
    },

    loadPageVersions: (pageName: string): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch({ type: ITEM_VERSIONS_LOAD_START });

            try {
                const url = `/api/admin/reports/versions/${pageName}`;

                const results = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `bearer ${state.auth.user?.access_token}`,
                    },
                });
                const items = await results.json();
                dispatch({ type: ITEM_VERSIONS_LOAD_END, items });
            } catch (err: any) {
                dispatch({
                    type: ITEM_VERSIONS_LOAD_END,
                    error: { message: err.message },
                });
            }
        };
    },

    selectPages: (pagesToSelect: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch({ type: SET_SELECTED_PAGES, pagesToSelect });
        };
    },
    unselectPages: (pagesToUnselect: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch({ type: REMOVE_SELECTED_PAGES, pagesToUnselect });
        };
    },
    unselectAllPages: (): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch({ type: REMOVE_ALL_SELECTED_PAGES });
        };
    },

    revertPageVersions: (pageName: any, versionNumber: any) => {
        return async (dispatch: any, getState: any) => {
            const state = getState();

            try {
                const url = `/api/admin/reports/revert/version/${pageName}/${versionNumber}`;

                const results = await fetch(url, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `bearer ${state.auth.user?.access_token}`,
                    },
                });
                const revertedItem = await results.json();

                if (!isNaN(revertedItem.VersionNumber)) {
                    dispatch({ type: REVERTED_VERSION });
                }
            } catch (err: any) {
                dispatch({
                    type: REVERTED_VERSION,
                    error: { message: err.message },
                });
            }
        };
    },

    setShowDeletedItems: (): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch({ type: SET_SHOW_DELETED_ITEMS });
        };
    },
};

export const reducer = (state = defaultState, action: AnyAction): StateShape => {
    switch (action.type) {
        case ITEMS_LOAD_START: {
            return produce(state, (draft) => {
                draft.itemsLoading = true;
                draft.items = [];
                draft.itemsLoadError = null;
            });
        }
        case ITEMS_LOAD_END: {
            return produce(state, (draft) => {
                draft.itemsLoading = false;
                draft.items = action.items;
                draft.itemsLoadError = action.error;
                draft.revertedVersion = false;
            });
        }
        case TEMPLATES_LOAD_START: {
            return produce(state, (draft) => {
                draft.templatesLoading = true;
                draft.templates = [];
                draft.templatesLoadError = null;
            });
        }
        case TEMPLATES_LOAD_END: {
            return produce(state, (draft) => {
                draft.templatesLoading = false;
                draft.templates = action.items;
                draft.templatesLoadError = action.error;
            });
        }
        case ITEM_VERSIONS_LOAD_START: {
            return produce(state, (draft) => {
                draft.itemVersionsLoading = true;
                draft.itemVersions = [];
                draft.itemVersionsLoadError = null;
            });
        }
        case ITEM_VERSIONS_LOAD_END: {
            return produce(state, (draft) => {
                draft.itemVersionsLoading = false;
                draft.itemVersions = action.items;
                draft.itemVersionsLoadError = action.error;
                draft.revertedVersion = false;
            });
        }
        case SET_SELECTED_PAGES: {
            return produce(state, (draft) => {
                draft.selectedPages = [...state.selectedPages, ...action.pagesToSelect];
            });
        }
        case REMOVE_SELECTED_PAGES: {
            return produce(state, (draft) => {
                draft.selectedPages = state.selectedPages.filter(
                    (page) => !action.pagesToUnselect.includes(page)
                );
            });
        }
        case REMOVE_ALL_SELECTED_PAGES: {
            return produce(state, (draft) => {
                draft.selectedPages = [];
            });
        }
        case REVERTED_VERSION: {
            return produce(state, (draft) => {
                draft.revertedVersion = true;
                draft.revertedVersionError = action.error;
            });
        }
        case SET_SHOW_DELETED_ITEMS: {
            return produce(state, (draft) => {
                draft.showDeletedItems = !state.showDeletedItems;
            });
        }
        default:
            return state;
    }
};
